import { jwtDecode } from "jwt-decode";
import { makeAutoObservable, runInAction } from "mobx";

import { AuthApi } from "./auth.api";
import { JwtPayload, LoginResponseDto } from "./auth.dto";
import { STORAGE_KEYS, TOKEN_CONFIG, TokenInfo } from "./auth.variant";

/**
 * Auth Service (MobX Store)
 * - JWT 토큰 상태 관리
 * - JWT 디코딩
 * - 토큰 만료 체크
 * - Storage 추상화
 * - 레거시 토큰 마이그레이션 지원
 */
class AuthService {
  private _tokenInfo: TokenInfo | null = null;
  private _isInitializing = true;

  constructor() {
    makeAutoObservable(this);

    this.loadTokenFromStorage();
  }

  get tokenInfo() {
    return this._tokenInfo;
  }

  get isInitializing() {
    return this._isInitializing;
  }

  setInitializing(value: boolean) {
    this._isInitializing = value;
  }

  get tokenStatus() {
    if (!this._tokenInfo) return "NONE";

    if (this.isTokenExpired(this._tokenInfo.expiresAt)) return "EXPIRED";

    if (
      this.isTokenExpired(
        this._tokenInfo.expiresAt,
        TOKEN_CONFIG.ACCESS_TOKEN_BUFFER
      )
    ) {
      return "ABOUT_TO_EXPIRE";
    }

    return "FRESH";
  }

  get isTokenInvalid() {
    return this.tokenStatus === "NONE" || this.tokenStatus === "EXPIRED";
  }

  get isTokenValid() {
    return !this.isTokenInvalid;
  }

  /**
   * 토큰 갱신이 필요한지 확인
   */
  get needsRefresh(): boolean {
    if (!this._tokenInfo) return false;

    return this.isTokenExpired(
      this._tokenInfo.expiresAt,
      TOKEN_CONFIG.ACCESS_TOKEN_BUFFER
    );
  }

  /**
   * Storage에서 토큰 정보 읽기
   */
  get localStorageInfo(): TokenInfo | null {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!accessToken || !refreshToken) {
        return null;
      }

      // JWT에서 만료시간 직접 추출
      const expiresAt = this.extractExpiryFromToken(accessToken);

      return {
        accessToken,
        refreshToken,
        expiresAt,
      };
    } catch (error) {
      console.error("Failed to load token from storage:", error);
      return null;
    }
  }

  /**
   * JWT에서 만료 시간 추출 (밀리초 단위로 변환)
   */
  extractExpiryFromToken(token: string): number {
    const payload = jwtDecode<JwtPayload>(token);
    return payload.exp * 1_000; // 초 → 밀리초
  }

  /**
   * 토큰이 만료되었거나 만료 임박 여부 확인
   */
  isTokenExpired(
    expiresAt: number,
    buffer = TOKEN_CONFIG.ACCESS_TOKEN_BUFFER
  ): boolean {
    return Date.now() >= expiresAt - buffer;
  }

  /**
   * 로그인 응답 데이터 저장
   */
  saveToken(dto: LoginResponseDto) {
    runInAction(() => {
      const expiresAt = this.extractExpiryFromToken(dto.accessToken);
      this._tokenInfo = {
        accessToken: dto.accessToken,
        refreshToken: dto.refreshToken,
        expiresAt,
      };
      this.setLocalStorageToken(dto.accessToken, dto.refreshToken);
    });
  }

  /**
   * Storage에서 토큰 정보 로드
   */
  loadTokenFromStorage() {
    const tokenInfo = this.localStorageInfo;
    if (tokenInfo) {
      this._tokenInfo = tokenInfo;
    }
  }

  /**
   * Token 초기화 (로그아웃)
   */
  clearToken() {
    this._tokenInfo = null;
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Storage에 토큰 저장
   */
  private setLocalStorageToken(accessToken: string, refreshToken: string) {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    } catch (error) {
      console.error("Failed to save token to storage:", error);
      throw error;
    }
  }

  /**
   * 로그인 (API + Service)
   */
  async login(id: string, password: string): Promise<LoginResponseDto> {
    const response = await AuthApi.login({
      id: id,
      password: password,
      userType: "TEACHER", // 출석 앱은 항상 선생님
      serviceType: "MATHFLAT", // 매쓰플랫 출석
    });

    this.saveToken(response);

    return response;
  }

  /**
   * 로그아웃 (API + Service)
   */
  async logout(): Promise<void> {
    try {
      const tokenInfo = this._tokenInfo;
      if (tokenInfo?.refreshToken) {
        await AuthApi.logout(tokenInfo.refreshToken);
      }
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      this.clearToken();
    }
  }

  /**
   * 토큰 재발급 (API + Service)
   */
  async reissue(): Promise<TokenInfo> {
    const tokenInfo = this._tokenInfo;
    if (!tokenInfo || !tokenInfo.refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await AuthApi.reissue(tokenInfo.refreshToken);

    this.saveToken(response);

    return this._tokenInfo!;
  }

  /**
   * 레거시 토큰 마이그레이션 (API + Service)
   */
  async migrateLegacyToken(): Promise<TokenInfo | null> {
    const legacyToken = localStorage.getItem(STORAGE_KEYS.LEGACY_ACCESS_TOKEN);

    if (!legacyToken) return null;

    try {
      const response = await AuthApi.migrateLegacyToken(legacyToken);

      this.saveToken(response);

      localStorage.removeItem(STORAGE_KEYS.LEGACY_ACCESS_TOKEN);

      return this._tokenInfo;
    } catch (error) {
      console.error("Failed to migrate legacy token:", error);
      // 마이그레이션 실패 시 레거시 토큰 삭제
      localStorage.removeItem(STORAGE_KEYS.LEGACY_ACCESS_TOKEN);
      return null;
    }
  }

  /**
   * 비밀번호 확인 (관리 옵션용)
   */
  async confirmPassword(password: string): Promise<boolean> {
    const { passwordMatched } = await AuthApi.checkPassword({ password });
    return passwordMatched;
  }

  /**
   * 비밀번호 재설정
   */
  async resetPassword(data: { loginId: string; email: string }) {
    await AuthApi.resetPassword(data);
  }
}

const authService = new AuthService();
export default authService;
